/**
 * BydVehicleCard — 比亚迪秦L 2024卓越版 车辆状态卡片
 * 参考：比亚迪汽车 App 首页布局
 *   - 顶部：车名 + 3D车辆插画
 *   - 数据行：总里程 | 电量% | 能效%
 *   - 状态行：熄火/启动 / 空调 / NFC / 蓝牙
 *   - 快捷操作：车门解锁、上锁、一键熄火、后备箱
 *   - 空调温度控制 + 地图位置
 *
 * 设计语言：xiaofeng-os 暗色玻璃态 + 比亚迪王朝金色系 + App 风格
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Car, Gauge, Battery, Thermometer, Lock, Unlock,
  Wind, MapPin, Wifi, WifiOff, Power, Zap, Droplets,
  Nfc, Bluetooth, Volume2, ChevronLeft, ChevronRight,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { toast } from 'sonner'; // Bug 35: 添加 toast 导入

// ── Types ──

export interface BydVehicleData {
  deviceName: string;
  online: boolean;
  vin: string;
  soc: number;
  rangeRemaining: number;
  energyEfficiency: number;       // 能效 %
  odometer: number;
  speed: number;
  gear: string;
  engineOn: boolean;
  motorRpm: number;
  tirePressureFL: number;
  tirePressureFR: number;
  tirePressureRL: number;
  tirePressureRR: number;
  doorsLocked: boolean;
  trunkOpen: boolean;
  windowsClosed: boolean;
  acOn: boolean;
  acTemp: number;
  interiorTemp: number;
  nfcOn: boolean;
  bluetoothOn: boolean;
  address?: string;
  latitude?: number;
  longitude?: number;
  lastUpdated?: string;
}

// ══════════════════════════════════════
// 环形仪表盘组件
// ══════════════════════════════════════

function GaugeRing({
  value, max, color, label, unit, size = 80,
}: {
  value: number; max: number; color: string; label: string; unit: string; size?: number;
}) {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(value / max, 1);
  const offset = circumference * (1 - progress);
  const strokeWidth = 6;
  const center = size / 2;

  return (
    <div className="flex flex-col items-center gap-1.5">
      <svg width={size} height={size} className="-rotate-90">
        {/* 背景环 */}
        <circle
          cx={center} cy={center} r={radius}
          fill="none" stroke="oklch(100% 0 0 / 0.06)" strokeWidth={strokeWidth}
        />
        {/* 进度环 */}
        <motion.circle
          cx={center} cy={center} r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          style={{ filter: `drop-shadow(0 0 6px ${color}33)` }}
        />
        {/* 中心文字 */}
        <text
          x={center} y={center}
          textAnchor="middle" dominantBaseline="central"
          className="fill-white/80 text-[15px] font-black"
          style={{ transform: 'rotate(90deg)', transformOrigin: `${center}px ${center}px` }}
        >
          {Math.round(value)}{unit}
        </text>
      </svg>
      <span className="text-[9px] text-white/25">{label}</span>
    </div>
  );
}

// ══════════════════════════════════════
// 快捷操作按钮（App 风格圆形图标）
// ══════════════════════════════════════

function ActionBtn({
  icon: Icon,
  label,
  active = false,
  accent = false,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  active?: boolean;
  accent?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 p-2.5 rounded-2xl transition-all duration-200 group cursor-pointer"
      style={{
        background: active
          ? (accent ? 'oklch(65% 0.15 25 / 0.15)' : 'oklch(65% 0.08 160 / 0.12)')
          : 'oklch(100% 0 0 / 0.03)',
        border: `1px solid ${active ? (accent ? 'oklch(65% 0.15 25 / 0.2)' : 'oklch(65% 0.08 160 / 0.12)') : 'oklch(100% 0 0 / 0.05)'}`,
      }}
    >
      <div className={`p-2 rounded-xl transition-all duration-200 ${
        active ? (accent ? 'bg-amber-500/15' : 'bg-green-500/10') : 'bg-white/[0.04] group-hover:bg-white/[0.07]'
      }`}>
        <Icon size={16} className={
          active
            ? (accent ? 'text-amber-400' : 'text-green-400')
            : 'text-white/30 group-hover:text-white/50'
        } />
      </div>
      <span className={`text-[9px] font-medium ${active ? 'text-white/50' : 'text-white/25'}`}>
        {label}
      </span>
    </button>
  );
}

// ══════════════════════════════════════
// 空调控制面板（App 风格）
// ══════════════════════════════════════

function AcControl({
  on,
  temp,
  interiorTemp,
  onToggle,
  onTempChange,
}: {
  on: boolean;
  temp: number;
  interiorTemp: number;
  onToggle: () => void;
  onTempChange: (delta: number) => void;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/[0.02] border border-white/[0.05]">
      <button
        onClick={onToggle}
        className={`p-2.5 rounded-xl transition-all duration-200 ${
          on ? 'bg-cyan-500/10 border border-cyan-400/15' : 'bg-white/[0.03] border border-white/[0.05]'
        }`}
      >
        <Wind size={15} className={on ? 'text-cyan-400' : 'text-white/20'} />
      </button>

      <div className="flex items-center gap-2">
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => onTempChange(-1)}
          className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/[0.04] text-white/30 hover:text-white/50 hover:bg-white/[0.07] transition-colors text-lg font-light"
        >
          −
        </motion.button>
        <div className="flex flex-col items-center min-w-[48px]">
          <span className="text-xl font-bold tracking-tight text-white/70">{temp}°</span>
          <span className="text-[8px] text-white/15">车内 {interiorTemp.toFixed(1)}°</span>
        </div>
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => onTempChange(1)}
          className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/[0.04] text-white/30 hover:text-white/50 hover:bg-white/[0.07] transition-colors text-lg font-light"
        >
          +
        </motion.button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════
// 位置信息条
// ══════════════════════════════════════

function LocationBar({ address }: { address?: string }) {
  return (
    <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-2xl bg-white/[0.02] border border-white/[0.05]">
      <MapPin size={13} className="text-blue-400/50 shrink-0" />
      <div className="min-w-0 flex-1">
        <span className="text-[11px] text-white/40 truncate block">
          {/* Bug 36: 硬编码兜底地址改为'定位中...' */}
          {address || '定位中...'}
        </span>
      </div>
      <div className="shrink-0 flex items-center gap-1">
        <span className="text-[8px] px-1.5 py-0.5 rounded-md bg-blue-500/10 text-blue-400/60 font-mono">NPS</span>
        <span className="text-[8px] text-white/15">推荐星级领劵</span>
      </div>
    </div>
  );
}

// ══════════════════════════════════════
// 主卡片组件
// ══════════════════════════════════════

interface BydVehicleCardProps {
  data: BydVehicleData;
}

export function BydVehicleCard({ data }: BydVehicleCardProps) {
  const [acTemp, setAcTemp] = useState(data.acTemp);
  const [localDoorsLocked, setDoorsLocked] = useState(data.doorsLocked);
  const [localTrunkOpen, setTrunkOpen] = useState(data.trunkOpen);
  const [localAcOn, setAcOn] = useState(data.acOn);

  const {
    deviceName, online, vin, soc, rangeRemaining, energyEfficiency, odometer,
    speed, gear, engineOn,
    tirePressureFL, tirePressureFR, tirePressureRL, tirePressureRR,
    doorsLocked, trunkOpen, windowsClosed,
    acOn, interiorTemp, nfcOn, bluetoothOn,
    address, lastUpdated,
  } = data;

  const vinShort = vin.length > 6 ? `***${vin.slice(-6)}` : vin;
  const odometerStr = odometer >= 10000
    ? `${(odometer / 10000).toFixed(1)}万km`
    : `${odometer.toLocaleString()}km`;

  // SOC 颜色
  const socColor = soc > 50 ? '#4ade80' : soc > 20 ? '#fbbf24' : '#f87171';
  const socGlow = soc > 50 ? 'oklch(65% 0.16 160 / 0.25)' : soc > 20 ? 'oklch(65% 0.16 70 / 0.25)' : 'oklch(60% 0.16 20 / 0.25)';

  // 更新时间
  const updateTime = lastUpdated
    ? (() => {
      const d = new Date(lastUpdated);
      const now = Date.now();
      const diffMin = Math.round((now - d.getTime()) / 60000);
      if (diffMin < 1) return '刚刚';
      if (diffMin < 60) return `${diffMin}分钟前`;
      const diffH = Math.floor(diffMin / 60);
      return `${diffH}小时前`;
    })()
    : '--:--';

  const handleTempChange = useCallback((delta: number) => {
    setAcTemp(prev => Math.max(16, Math.min(30, prev + delta)));
  }, []);

  return (
    <motion.div
      className="relative rounded-3xl overflow-hidden border border-white/[0.06]"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* ── 顶部渐变背景区（车辆展示区）── */}
      <div className="relative pt-5 pb-3 px-5"
        style={{
          background: 'linear-gradient(180deg, oklch(35% 0.06 240 / 0.4) 0%, oklch(25% 0.03 260 / 0.2) 60%, transparent 100%)',
        }}
      >
        {/* 顶栏：车名 + 图标 */}
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-bold text-white/75 tracking-wide">{deviceName}</h3>
          <div className="flex items-center gap-2.5">
            <Car size={16} className="text-white/20" />
            {/* Bug 35: 添加 onClick 事件 */}
            <button onClick={() => toast.info('音量控制功能开发中')} className="p-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.10] transition-colors">
              <Volume2 size={13} className="text-white/30" />
            </button>
          </div>
        </div>

        {/* 数据行：里程 | 电量 | 能效 */}
        <div className="flex items-baseline gap-3 mb-3">
          <motion.span className="text-2xl font-black tracking-tight text-white/80"
            key={odometer}
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.35 }}>
            {odometerStr}
          </motion.span>
          <div className="flex items-center gap-1.5">
            <Zap size={13} style={{ color: socColor }} />
            <motion.span className="text-sm font-bold" style={{ color: socColor }}
              key={soc} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
              {Math.round(soc)}%
            </motion.span>
          </div>
          <div className="flex items-center gap-1">
            <Droplets size={12} className="text-sky-400/60" />
            <span className="text-xs font-medium text-sky-400/60">{energyEfficiency}%</span>
          </div>
        </div>

        {/* 状态指示行 */}
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${
            engineOn ? 'bg-red-500/15 text-red-400' : 'bg-white/[0.06] text-white/35'
          }`}>
            {engineOn ? '已启动' : '已熄火'}
          </span>
          <div className="flex items-center gap-1">
            <Wind size={11} className={acOn ? 'text-cyan-400/60' : 'text-white/15'} />
            <span className={`text-[10px] ${acOn ? 'text-cyan-400/60' : 'text-white/15'}`}>
              {acOn ? `${acTemp}°C空调` : '空调关'}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Bluetooth size={10} className={bluetoothOn ? 'text-blue-400/50' : 'text-white/10'} />
            <span className={`text-[9px] ${bluetoothOn ? 'text-blue-400/50' : 'text-white/10'}`}>蓝牙</span>
          </div>
          <div className="flex items-center gap-1">
            <Nfc size={10} className={nfcOn ? 'text-purple-400/50' : 'text-white/10'} />
            <span className={`text-[9px] ${nfcOn ? 'text-purple-400/50' : 'text-white/10'}`}>NFC</span>
          </div>
        </div>

        {/* 更新时间 */}
        <span className="text-[9px] text-white/12">更新于: {updateTime}</span>

        {/* ── 双环仪表盘（电量 + 能效）── */}
        <div className="flex items-center justify-center gap-8 py-4">
          {/* 电量环 */}
          <GaugeRing value={soc} max={100} color={socColor} label="电量" unit="%" size={80} />
          {/* 能效环 */}
          <GaugeRing value={energyEfficiency} max={100} color="#60a5fa" label="能效" unit="%" size={80} />
          {/* 续航里程 */}
          <div className="flex flex-col items-center gap-1">
            <span className="text-2xl font-black text-white/80">{rangeRemaining}</span>
            <span className="text-[10px] text-white/30">km</span>
            <span className="text-[9px] text-white/15">续航里程</span>
          </div>
        </div>
      </div>

      {/* ── 分割线 ── */}
      <div className="h-px mx-5" style={{ background: 'linear-gradient(90deg, transparent, oklch(100% 0 0 / 0.08), transparent)' }} />

      {/* ── 快捷操作按钮行 ── */}
      <div className="px-5 py-4">
        <div className="grid grid-cols-4 gap-2">
                    <ActionBtn icon={Unlock} label="车门解锁" active={!localDoorsLocked} onClick={() => setDoorsLocked(false)} />
                    <ActionBtn icon={Lock} label="车门上锁" active={localDoorsLocked} onClick={() => setDoorsLocked(true)} />
                    <ActionBtn icon={Power} label="一键熄火" accent active={!engineOn} onClick={() => {}} />
                    <ActionBtn icon={Car} label="后备箱" active={localTrunkOpen} onClick={() => setTrunkOpen(!localTrunkOpen)} />
        </div>
      </div>

      {/* ── 空调 + 位置 ── */}
      <div className="px-5 pb-4 space-y-2.5">
        <AcControl
          on={localAcOn} temp={acTemp} interiorTemp={interiorTemp}
          onToggle={() => setAcOn(!localAcOn)}
          onTempChange={handleTempChange}
        />
        <LocationBar address={address} />
      </div>

      {/* ── 底部工具栏 ── */}
      <div className="flex items-center justify-around py-3 px-5 border-t border-white/[0.04]"
        style={{ background: 'oklch(100% 0 0 / 0.015)' }}>
        {[
          { icon: Zap, label: '社区', sub: '' },
          { icon: Wind, label: '商城', sub: '' },
          { icon: Car, label: '王朝', sub: true },
          { icon: MapPin, label: '服务', sub: '' },
          { icon: null, label: '我的', sub: '' },
        ].map((item, i) => (
          <button key={i} className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-xl transition-colors ${
            item.sub ? 'text-amber-500' : 'text-white/20 hover:text-white/40'
          }`}>
            {item.icon && <item.icon size={16} />}
            {!item.icon && i === 4 && <span className="text-lg">☺</span>}
            <span className={`text-[9px] font-medium ${item.sub ? 'font-bold' : ''}`}>{item.label}</span>
          </button>
        ))}
      </div>

      {/* ── 离线遮罩 ── */}
      <AnimatePresence>
        {!online && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 rounded-3xl bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-30"
          >
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/15 border border-red-400/20">
              <WifiOff size={14} className="text-red-400/70" />
              <span className="text-xs font-bold text-red-400/70">车辆离线</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ══════════════════════════════════════
// 动态 Mock 数据 Hook
// ══════════════════════════════════════

export function useBydMockData(): BydVehicleData {
  const [data, setData] = useState<BydVehicleData>(() => ({
    deviceName: '秦L 2024 卓越版',
    online: true,
    vin: 'LC0C76C47R0001234',
    soc: 62,
    rangeRemaining: 480,
    energyEfficiency: 89,
    odometer: 1779,
    speed: 0,
    gear: 'P',
    engineOn: false,
    motorRpm: 0,
    tirePressureFL: 2.5,
    tirePressureFR: 2.5,
    tirePressureRL: 2.4,
    tirePressureRR: 2.4,
    doorsLocked: true,
    trunkOpen: false,
    windowsClosed: true,
    acOn: false,
    acTemp: 19,
    interiorTemp: 28.5,
    nfcOn: true,
    bluetoothOn: true,
    address: undefined,
    lastUpdated: new Date().toISOString(),
  }));

  useEffect(() => {
    const tick = () => {
      setData(prev => ({
        ...prev,
        // 电量缓慢下降（驻车耗电，约每周掉 5%）
        soc: Math.max(10, +(prev.soc - 0.0005).toFixed(1)),
        rangeRemaining: Math.round((Math.max(10, prev.soc - 0.0005) / 100) * 780),
        // 胎压微小波动
        tirePressureFL: +(prev.tirePressureFL + (Math.random() - 0.5) * 0.02).toFixed(2),
        tirePressureFR: +(prev.tirePressureFR + (Math.random() - 0.5) * 0.02).toFixed(2),
        tirePressureRL: +(prev.tirePressureRL + (Math.random() - 0.5) * 0.02).toFixed(2),
        tirePressureRR: +(prev.tirePressureRR + (Math.random() - 0.5) * 0.02).toFixed(2),
        // 车内温度缓慢变化
        interiorTemp: +(prev.interiorTemp + (Math.random() - 0.5) * 0.08).toFixed(1),
        lastUpdated: new Date().toISOString(),
      }));
    };

    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, []);

  return data;
}
